import { useNewCode } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { EmptyState } from '@calendly/table';
import { Avatar } from '@calendly/ui/components/avatar';
import { IconButton } from '@calendly/ui/components/icon-button';
import { StarIcon, StarFullIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { Loader } from '@calendly/ui/components/loader';
import { Table, Header, HeaderCell, Body, Row, Cell } from '@calendly/ui/components/table';
import { BodyText } from '@calendly/ui/components/typography';
import { VisuallyHidden } from '@calendly/ui/components/visually-hidden';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorAlert } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToggleContactFavoriteMutation } from 'features/contacts/api';
import { EventTypesShareProvider } from 'features/event-types-share';
import { openSendPopupForContactsList, INVITEE_EXPERIENCE_SEND_FROM_CALENDLY_Q3 } from 'features/send_from_calendly';
import { useTrack } from 'hooks/analytics_events';
import { getInitials } from 'shared/utils';
import { trackShareAvailabilityEvent, trackScheduleInRealTimeEvent, trackContactProfileView, trackFlowStartedEvent } from '../analytics';
import { SCHEDULE_WITH_CONTACT_PROVIDER } from '../constants';
import { ScheduleWithAContactProvider } from '../schedule_with_a_contact';
import { isShareAvailabilityFeatureEnabledSelector } from '../selectors';
import { ActionsDropdown } from './actions_dropdown';
import { FullStoryExclude, FullStoryMask } from '../../../shared/fullstory_privacy';
import { CreateEventButton } from '../shared/create_event_button';
import { ShareAvailabilityButton } from '../shared/share_availability_button';
import { setCurrentContactProfile, setSelectedContactDrawer, toggleContactDrawerOpen } from '../slice';
const emailCss = "e1li9jtk";
const Email = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emailCss, props.className)} />;
});
const contactNameIsPhoneCss = "c5371rg";
const contactNameCss = "cdoeldy";
const ContactName = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(contactNameCss, {
    [contactNameIsPhoneCss]: isPhone
  }, props.className)} />;
});
const mobileContactDetailsCss = "m138y4mi";
const MobileContactDetails = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(mobileContactDetailsCss, props.className)} />;
});
const mobileActionsContainerCss = "m155qn29";
const MobileActionsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(mobileActionsContainerCss, props.className)} />;
});
const activeButtonContainerCss = "a1buw3ih";
const ActiveButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(activeButtonContainerCss, props.className)} />;
});
const emptyStateContainerCss = "e6u77ij";
const EmptyStateContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emptyStateContainerCss, props.className)} />;
});
const createNewLinkCss = "c3jm1xe";
const CreateNewLink = forwardRef((props, ref) => <Link ref={ref} {...props} className={classNames(createNewLinkCss, props.className)} />);
const emptyStateTextWrapperIsPhoneCss = "e16zd7lx";
const emptyStateTextWrapperCss = "e1p9y5oq";
const EmptyStateTextWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(emptyStateTextWrapperCss, {
    [emptyStateTextWrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledIconButtonCss = "s13r2lev";
const StyledIconButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(styledIconButtonCss, props.className)} />);
export const TableView = ({
  data,
  isLoading,
  isError
}) => {
  const [toggleContactFavorite] = useToggleContactFavoriteMutation();
  const [isFlowStarted, setIsFlowStarted] = useState(false);
  const [isShareStarted, setIsShareStarted] = useState(false);
  const [sharedContact, setIsSharedContact] = useState(null);
  const isMobile = useDeviceType('isPhone');
  const isTablet = useDeviceType('isTablet');
  const track = useTrack();
  const isDesktop = useDeviceType('isDesktopUp');
  const dispatch = useDispatch();
  const isEmptyState = (data?.contacts?.length ?? 0) < 1;
  const showHeader = isDesktop || isTablet && !isEmptyState;
  const isShareAvailabilityFeatureEnabled = useSelector(isShareAvailabilityFeatureEnabledSelector);
  const isButtonsExperimentEnabled = useNewCode('org_expansion_share_cta_feature')?.enabled;
  const isSendFromCalendlyEnabled = useNewCode(INVITEE_EXPERIENCE_SEND_FROM_CALENDLY_Q3)?.enabled;
  const openProfile = contact => {
    dispatch(setCurrentContactProfile(contact));
    trackContactProfileView({
      actionType: 'contact_list',
      track
    });
  };
  const createMobileTableView = contact => <Row key={contact.uuid}>
      <Cell>
        <MobileContactDetails onClick={() => openProfile(contact)}>
          <FullStoryExclude>
            <Avatar letter={getInitials(contact.name)} />
          </FullStoryExclude>
          <ContactName>
            {' '}
            <FullStoryMask>
              {contact.name}
            </FullStoryMask>
          </ContactName>
        </MobileContactDetails>
      </Cell>
      <Cell align="right">
        <MobileActionsContainer>
          <IconButton size="small" title="Favorite" onClick={() => toggleContactFavorite({
          contact: {
            uuid: contact.uuid,
            favorite: !contact.favorite
          }
        })}>
            {contact.favorite ? <StarFullIcon color={colorAlert} size="small" inline spacingBottom={12} /> : <StarIcon size="small" inline spacingBottom={12} />}
          </IconButton>
          <ActionsDropdown contact={contact} />
        </MobileActionsContainer>
      </Cell>
    </Row>;
  const openNewContactDrawer = () => {
    dispatch(setSelectedContactDrawer('NEW_CONTACT_FORM'));
    dispatch(toggleContactDrawerOpen());
  };
  return isLoading ? <Loader /> : <div>
          <Table borderless={isEmptyState} noContainerBorder={isEmptyState && !isDesktop}>
            {showHeader && <Header>
                  <HeaderCell hugContent>
                    <VisuallyHidden>Avatar</VisuallyHidden>
                  </HeaderCell>
                  <HeaderCell>
                    {I18n.t('publisher.contacts.table.headers.name')}
                  </HeaderCell>
                  <HeaderCell>
                    {I18n.t('publisher.contacts.table.headers.next_meeting')}
                  </HeaderCell>
                  <HeaderCell>
                    {I18n.t('publisher.contacts.table.headers.last_meeting')}
                  </HeaderCell>
                  <HeaderCell hugContent>
                    <VisuallyHidden>Schedule or Share Availability</VisuallyHidden>
                  </HeaderCell>
                  <HeaderCell>
                    <VisuallyHidden>{I18n.t('publisher.components.table.action_menu')}</VisuallyHidden>
                  </HeaderCell>
                </Header>}
            <Body>
              {!isMobile && !isError && !isEmptyState && data?.contacts.map(contact => <Row id="contact-row" key={contact.uuid} onClick={() => openProfile(contact)}>
                    <Cell>
                      <FullStoryExclude>
                        <Avatar letter={getInitials(contact.name)} />
                      </FullStoryExclude>
                    </Cell>
                    <Cell>
                      <ContactName>
                        <div>
                          <FullStoryMask>
                            {contact.name}
                          </FullStoryMask>
                          <StyledIconButton size="small" aria-label="Favorite" onClick={() => toggleContactFavorite({
                  contact: {
                    uuid: contact.uuid,
                    favorite: !contact.favorite
                  }
                })}>
                            {contact.favorite ? <StarFullIcon data-testid={`star-full-icon-${contact.uuid}`} color={colorAlert} size="small" inline spacingBottom={12} /> : <StarIcon data-testid={`star-empty-icon-${contact.uuid}`} size="small" inline spacingBottom={12} />}
                          </StyledIconButton>
                          <Email>
                            <FullStoryMask>
                              {contact.email}
                            </FullStoryMask>
                          </Email>
                        </div>
                      </ContactName>
                    </Cell>
                    <Cell>
                      {contact.nextEvent && <>
                          <BodyText>{contact.nextEvent.name}</BodyText>
                          <BodyText>{contact.nextEvent.date}</BodyText>
                        </>}
                    </Cell>
                    <Cell>
                      {contact.lastEvent && <>
                          <BodyText>{contact.lastEvent.name}</BodyText>
                          <BodyText>{contact.lastEvent.date}</BodyText>
                        </>}
                    </Cell>
                    <Cell>
                      <ActiveButtonContainer>
                        <CreateEventButton onClick={() => {
                setIsSharedContact(contact);
                setIsShareStarted(true);
                trackScheduleInRealTimeEvent({
                  contactUuid: contact.uuid,
                  source: 'contact_list',
                  step: 'open_modal',
                  track
                });
                trackFlowStartedEvent({
                  flowLocation: 'contacts_list',
                  track
                });
              }} isButtonExperimentEnabled={isButtonsExperimentEnabled} />
                        {isShareAvailabilityFeatureEnabled && <ShareAvailabilityButton onClick={() => {
                if (isSendFromCalendlyEnabled) {
                  dispatch(openSendPopupForContactsList({
                    contact,
                    source: SCHEDULE_WITH_CONTACT_PROVIDER.PARENT_COMPONENT.CONTACT_LIST
                  }));
                } else {
                  setIsSharedContact(contact);
                  setIsFlowStarted(true);
                }
                trackShareAvailabilityEvent({
                  contactUuid: contact.uuid,
                  source: 'contact_list',
                  step: 'open_modal',
                  track
                });
              }} isButtonExperimentEnabled />}
                      </ActiveButtonContainer>
                    </Cell>
                    <Cell align="right">
                      <ActionsDropdown contact={contact} />
                    </Cell>
                  </Row>)}
              {isMobile && !isError && !isEmptyState && data?.contacts.map(contact => createMobileTableView(contact))}
              {isEmptyState && <EmptyState>
                  <EmptyStateContainer>
                    <EmptyStateTextWrapper>
                      {`${I18n.t('publisher.contacts.table.no_contacts_message')} `}
                      <CreateNewLink as="button" onClick={() => openNewContactDrawer()} underline={false}>
                        {I18n.t('publisher.contacts.table.no_contacts_message_link')}
                      </CreateNewLink>
                    </EmptyStateTextWrapper>
                  </EmptyStateContainer>
                </EmptyState>}
            </Body>
          </Table>
          <EventTypesShareProvider isFlowStarted={isFlowStarted} contact={sharedContact} onFlowFinish={() => {
      setIsFlowStarted(false);
      setIsSharedContact(null);
    }} source={SCHEDULE_WITH_CONTACT_PROVIDER.PARENT_COMPONENT.CONTACT_LIST} />
          <ScheduleWithAContactProvider isFlowStarted={isShareStarted} contact={sharedContact} onFlowFinish={() => {
      setIsShareStarted(false);
      setIsSharedContact(null);
    }} parentSource={SCHEDULE_WITH_CONTACT_PROVIDER.PARENT_COMPONENT.CONTACT_LIST} />
        </div>;
};

require("./contacts_table.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./contacts_table.js");